import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

export const Select = SelectPrimitive.Root;
export const SelectValue = SelectPrimitive.Value;
export function SelectTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        "flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20 aria-invalid:focus-visible:border-destructive aria-invalid:focus-visible:ring-destructive/30 bg-card px-3 text-sm text-foreground shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}
export function SelectContent({
  children,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        position="popper"
        sideOffset={4}
        className={cn(
          "z-[100] max-h-[min(20rem,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-border bg-card text-foreground shadow-md",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton className="flex justify-center py-1">
          <ChevronUp className="size-4" />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport className="p-1">
          {children}
        </SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="flex justify-center py-1">
          <ChevronDown className="size-4" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}
export function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={cn(
        "relative flex min-h-10 cursor-default select-none items-center rounded-sm py-2 ps-3 pe-9 text-sm outline-none focus:bg-secondary focus:text-secondary-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="absolute end-3">
        <Check className="size-4" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

type OptionProps = {
  value?: string | number;
  disabled?: boolean;
  label?: string;
  children?: React.ReactNode;
};
// Existing localized option descriptions are consumed as data; only Radix items render.
function renderOptions(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement<OptionProps>(child)) return null;
    if (child.type === "option")
      return (
        <SelectItem
          value={String(child.props.value) || "__empty__"}
          disabled={child.props.disabled}
        >
          {child.props.children}
        </SelectItem>
      );
    if (child.type === "optgroup")
      return (
        <SelectPrimitive.Group>
          <SelectPrimitive.Label className="px-3 py-2 text-sm font-semibold text-muted-foreground">
            {child.props.label}
          </SelectPrimitive.Label>
          {renderOptions(child.props.children)}
        </SelectPrimitive.Group>
      );
    return renderOptions(child.props.children);
  });
}
type SelectFieldProps = Omit<
  React.ComponentProps<typeof SelectPrimitive.Trigger>,
  "value" | "defaultValue" | "onChange"
> & {
  value?: string;
  defaultValue?: string;
  onValueChange: (value: string) => void;
  name?: string;
  required?: boolean;
};
export function SelectField({
  value,
  defaultValue,
  onValueChange,
  name,
  required,
  disabled,
  children,
  ...triggerProps
}: SelectFieldProps) {
  return (
    <Select
      value={value === undefined ? undefined : value || "__empty__"}
      defaultValue={
        defaultValue === undefined ? undefined : defaultValue || "__empty__"
      }
      onValueChange={(value) =>
        onValueChange(value === "__empty__" ? "" : value)
      }
      name={name}
      required={required}
      disabled={disabled}
    >
      <SelectTrigger {...triggerProps}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>{renderOptions(children)}</SelectContent>
    </Select>
  );
}
